/**
 * Shared order and technical configuration for the preview and setup panels.
 * Settings (McpPane) and /ai-dla-ksiegowych read the same constants, so a
 * snippet fixed here is fixed on every surface that shows it.
 */
export const MCP_CLIENTS = ['chatgpt', 'claudeDesktop', 'cursor', 'claudeCode'] as const;
export type McpClient = typeof MCP_CLIENTS[number];
/**
 * The setup section also covers OpenAI Codex. The hero preview keeps the four
 * clients above - Codex was approved for the setup section only (2026-10-06).
 */
export const MCP_SETUP_CLIENTS = [...MCP_CLIENTS, 'codex'] as const;
export type McpSetupClient = typeof MCP_SETUP_CLIENTS[number];
export const MCP_URL = 'https://api.prawmi.pl/mcp';

/**
 * PrawMi's page in the Claude Connectors Directory (live since 2026-10-05).
 * Opening it and clicking Connect is the primary path for claude.ai and the
 * Claude apps; pasting MCP_URL as a custom connector is the fallback.
 * Linked without utm_* parameters.
 */
export const CLAUDE_DIRECTORY_URL = 'https://claude.ai/directory/prawmi';

export const MCP_SNIPPETS: Record<McpSetupClient, string> = {
  chatgpt: MCP_URL,
  claudeDesktop: MCP_URL,
  cursor: `{
  "mcpServers": {
    "prawmi": {
      "url": "${MCP_URL}",
      "headers": { "X-API-Key": "prawmi_live_..." }
    }
  }
}`,
  // The form `claude mcp add --help` documents; `--url` is not a flag there.
  claudeCode: `claude mcp add --transport http prawmi ${MCP_URL} --header "X-API-Key: prawmi_live_..."`,
  // `codex mcp add --help` (codex-cli 0.160.1): a streamable HTTP server takes
  // --url. Without a bearer token Codex probes the server's OAuth metadata and
  // starts the browser login right away; `codex mcp login` repeats it.
  codex: `codex mcp add prawmi --url ${MCP_URL}`,
};

/** Re-runs the OAuth login, e.g. after editing config.toml by hand. */
export const CODEX_LOGIN_COMMAND = 'codex mcp login prawmi';

/** What `codex mcp add prawmi --url ...` writes to ~/.codex/config.toml. */
export const CODEX_CONFIG_TOML = `[mcp_servers.prawmi]
url = "${MCP_URL}"`;

/**
 * API-key alternative to OAuth: Codex sends the variable's value as
 * `Authorization: Bearer`, which /mcp accepts like X-API-Key. In config.toml
 * this adds `bearer_token_env_var = "PRAWMI_API_KEY"`.
 */
export const CODEX_API_KEY_SNIPPET = `export PRAWMI_API_KEY=prawmi_live_...
codex mcp add prawmi --url ${MCP_URL} --bearer-token-env-var PRAWMI_API_KEY`;

/** What the hero preview shows: Claude is connected from the directory page. */
export const MCP_PREVIEW_SNIPPETS: Record<McpClient, string> = {
  ...MCP_SNIPPETS,
  claudeDesktop: CLAUDE_DIRECTORY_URL,
};

export const usesSetupSteps = (client: McpSetupClient) =>
  client === 'chatgpt' || client === 'claudeDesktop';
